import { useTranslation } from 'react-i18next';
import { AiConversationFragmentFragment } from 'graphql-common';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import Input from '@lib/components/Input/Input';
import Typography from '@lib/components/Typography/Typography';
import { materialSymbolsOutlined } from '@lib/enums/common';
import ChatHistoryItem from './ChatHistoryItem';
import styles from './AiAssistant.module.scss';

type Props = {
  conversations: AiConversationFragmentFragment[];
  selectedConversationId: string | null;
  // The chat just created this session — animates its sidebar item in.
  newConversationId?: string | null;
  // A chat whose first turn failed, so no generated title is coming for it.
  untitledConversationId?: string | null;
  search: string;
  onSearchChange: (value: string) => void;
  onSelect: (id: string | null) => void;
  // Requests the rename modal for the given chat (opened by the modal host).
  onRename: (id: string) => void;
  onDestroy: (id: string) => void;
  // The chat currently being destroyed, so its actions menu can be hidden
  // right away instead of staying open until the mutation settles.
  destroyingId?: string | null;
};

function ChatSidebar(props: Props) {
  const {
    conversations,
    selectedConversationId,
    newConversationId,
    untitledConversationId,
    search,
    onSearchChange,
    onSelect,
    onRename,
    onDestroy,
    destroyingId,
  } = props;
  const { t } = useTranslation();

  // No conversations at all (fresh account) vs. a search that matched nothing —
  // distinct empty states. The search box stays mounted for the no-results case
  // so the user can clear or edit the query.
  const isEmpty = !conversations.length && !search;
  const hasNoResults = !conversations.length && !!search;

  // The panel body below the search box: a no-results notice or the chat list.
  // Split out so the top-level `isEmpty ?` stays a single (non-nested) ternary.
  const listBody = hasNoResults ? (
    <div className={styles.sidebarEmpty}>
      <Typography variant="caption" className={styles.sidebarEmptyText}>
        {t('no-results-found')}
      </Typography>
    </div>
  ) : (
    <div className={styles.sidebarList}>
      <Typography variant="caption" className={styles.sidebarListLabel}>
        {t('ai-chats-history')}
      </Typography>
      {conversations.map((conversation) => (
        <ChatHistoryItem
          key={conversation.id}
          conversation={conversation}
          isActive={conversation.id === selectedConversationId}
          isNew={conversation.id === newConversationId}
          titleUnavailable={conversation.id === untitledConversationId}
          isDestroying={conversation.id === destroyingId}
          onSelect={onSelect}
          onRename={onRename}
          onDestroy={onDestroy}
        />
      ))}
    </div>
  );

  return (
    <div className={styles.sidebar}>
      <div className={styles.sidebarTop}>
        <Button
          buttonText={t('ai-new-chat')}
          buttonType={ButtonTypes.primaryOutlined}
          leftIcon="edit_square"
          leftIconClassName={materialSymbolsOutlined}
          fullWidth
          onClick={() => onSelect(null)}
        />
        {!isEmpty && (
          <Input
            name="ai-search-chat"
            placeholder={t('ai-search-chat')}
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            leftIcon="search"
          />
        )}
      </div>
      {isEmpty ? (
        <div className={styles.sidebarEmpty}>
          <Typography
            variant="body"
            strong
            className={styles.sidebarEmptyTitle}
          >
            {t('ai-no-chat-history')}
          </Typography>
          <Typography variant="caption" className={styles.sidebarEmptyText}>
            {t('ai-no-chat-history-desc')}
          </Typography>
        </div>
      ) : (
        listBody
      )}
    </div>
  );
}

export default ChatSidebar;
